import { Calendar, Clock, Info, Zap, type LucideIcon } from "lucide-react";

export type ExpiryConfigType = "no-expiry" | "duration" | "fixed-date";
export type ExpiryDurationUnit = "days" | "weeks" | "months";

export interface ExpiryConfigValue {
  type: ExpiryConfigType;
  durationDays: number;
  durationUnit: ExpiryDurationUnit;
  fixedDate: string;
}

export const DEFAULT_EXPIRY_CONFIG: ExpiryConfigValue = {
  type: "no-expiry",
  durationDays: 7,
  durationUnit: "days",
  fixedDate: "",
};

const EXPIRY_OPTIONS: {
  type: ExpiryConfigType;
  label: string;
  description: string;
  icon: LucideIcon;
}[] = [
  { type: "no-expiry", label: "No Expiry", description: "Links never expire", icon: Zap },
  { type: "duration", label: "Duration", description: "Expires after N days", icon: Clock },
  {
    type: "fixed-date",
    label: "Fixed Date",
    description: "Set a specific date",
    icon: Calendar,
  },
];

const DURATION_OPTIONS = [7, 14, 30, 60, 90];

function formatFixedDate(value: string) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function getExpiryConfigSummary(value: ExpiryConfigValue): string {
  if (value.type === "no-expiry") return "Links never expire";
  if (value.type === "duration")
    return `Links expires after ${value.durationDays} ${value.durationUnit || "days"}`;
  return value.fixedDate
    ? `Links expires on ${formatFixedDate(value.fixedDate)}`
    : "Set an expiry date above";
}

interface ExpiryConfigCardProps {
  value: ExpiryConfigValue;
  onChange: (value: ExpiryConfigValue) => void;
}

export default function ExpiryConfigCard({ value, onChange }: ExpiryConfigCardProps) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
      <p className="text-[16px] font-bold text-[#1E1E1E]">
        Referral Code/Link Expiry Configuration
      </p>
      <p className="text-[13px] text-[#758089] mt-[2px]">
        Choose how referral links expire, changes apply to all new generated links
      </p>

      <div className="mt-[20px] grid grid-cols-1 sm:grid-cols-3 gap-[12px]">
        {EXPIRY_OPTIONS.map((option) => {
          const Icon = option.icon;
          const active = value.type === option.type;
          return (
            <button
              key={option.type}
              type="button"
              onClick={() => onChange({ ...value, type: option.type })}
              className={`rounded-[8px] border p-[16px] text-center ${
                active ? "border-[#734792]" : "border-[#E4E4E7]"
              }`}
            >
              <span
                className={`mx-auto flex size-[36px] items-center justify-center rounded-full ${
                  active ? "bg-[#734792] text-white" : "bg-[#F4F4F5] text-[#A1A1AA]"
                }`}
              >
                <Icon className="size-[16px]" />
              </span>
              <p className="mt-[10px] text-[14px] font-bold text-[#1E1E1E]">{option.label}</p>
              <p className="text-[13px] text-[#758089] mt-[2px]">{option.description}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-[16px] rounded-[8px] bg-[#F4F4F5] p-[16px]">
        {value.type === "no-expiry" && (
          <p className="inline-flex items-center gap-[8px] text-[13px] text-[#3F3F46]">
            <Info className="size-[14px]" />
            Links will remain active indefinitely until manually revoked
          </p>
        )}

        {value.type === "duration" && (
          <>
            <p className="text-[13px] font-medium text-[#1E1E1E]">Set expiry duration</p>
            <div className="mt-[10px] flex flex-wrap gap-[8px]">
              {DURATION_OPTIONS.map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => onChange({ ...value, durationDays: days, durationUnit: "days" })}
                  className={`rounded-[8px] px-[14px] py-[8px] text-[13px] font-medium ${
                    value.durationDays === days && value.durationUnit === "days"
                      ? "bg-[#734792] text-white"
                      : "bg-white border border-[#E4E4E7] text-[#3F3F46]"
                  }`}
                >
                  {days} days
                </button>
              ))}
            </div>
            <p className="mt-[10px] inline-flex items-center gap-[8px] text-[13px] text-[#3F3F46]">
              <Info className="size-[14px]" />
              Duration is counted from the moment the link is generated
            </p>
          </>
        )}

        {value.type === "fixed-date" && (
          <>
            <p className="text-[13px] font-medium text-[#1E1E1E]">Set expiry date</p>
            <div className="relative mt-[10px] max-w-[220px]">
              <input
                type="date"
                value={value.fixedDate}
                onChange={(e) => onChange({ ...value, fixedDate: e.target.value })}
                className="h-[40px] w-full rounded-[8px] border border-[#E4E4E7] bg-white pl-[12px] pr-[36px] text-[14px] text-[#1E1E1E] outline-none"
              />
              <Calendar className="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2 size-[16px] text-[#A1A1AA]" />
            </div>
            <p className="mt-[10px] inline-flex items-center gap-[8px] text-[13px] text-[#3F3F46]">
              <Info className="size-[14px]" />
              All links generated before this date will expire on this date
            </p>
          </>
        )}
      </div>
    </div>
  );
}
